import { api } from "@/lib/api";

export async function fingerprint(file) {
  const MB = 1048576;
  const head = await file.slice(0, MB).arrayBuffer();
  const tail = file.size > MB ? await file.slice(-MB).arrayBuffer() : new ArrayBuffer(0);
  const buf = new Uint8Array(head.byteLength + tail.byteLength + 16);
  buf.set(new Uint8Array(head), 0); buf.set(new Uint8Array(tail), head.byteLength);
  new DataView(buf.buffer).setFloat64(head.byteLength + tail.byteLength, file.size);
  const h = await crypto.subtle.digest("SHA-256", buf);
  return [...new Uint8Array(h)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

async function makeThumb(file) {
  const c = document.createElement("canvas");
  const draw = (src, w, h) => { const k = Math.min(1, 480 / Math.max(w, h)); c.width = w * k; c.height = h * k; c.getContext("2d").drawImage(src, 0, 0, c.width, c.height); };
  if (mimeOf(file).startsWith("image/")) { const b = await createImageBitmap(file); draw(b, b.width, b.height); }
  else {
    const v = document.createElement("video"); v.muted = true; v.playsInline = true; v.preload = "auto"; v.src = URL.createObjectURL(file);
    await new Promise((ok, no) => { v.onloadeddata = ok; v.onerror = no; setTimeout(no, 15000); });
    v.currentTime = Math.min(1, v.duration / 2 || 0);
    await new Promise((ok) => { v.onseeked = ok; setTimeout(ok, 4000); });
    await new Promise((ok) => { setTimeout(ok, 800); v.requestVideoFrameCallback?.(() => ok()); });
    draw(v, v.videoWidth, v.videoHeight); URL.revokeObjectURL(v.src);
  }
  return new Promise((ok) => c.toBlob(ok, "image/jpeg", 0.8));
}

const mimeOf = (f) => f.type || ({ jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png", heic: "image/heic", gif: "image/gif", webp: "image/webp", mp4: "video/mp4", mov: "video/quicktime", webm: "video/webm", m4v: "video/mp4", mkv: "video/x-matroska" })[f.name.split(".").pop().toLowerCase()] || "";

export const isMedia = (f) => /^(image|video)\//.test(mimeOf(f));

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const waitOnline = () => (navigator.onLine === false ? new Promise((r) => window.addEventListener("online", r, { once: true })) : Promise.resolve());
const retryable = (e) => !e?.response || e.response.status >= 500 || [408, 409, 425, 429].includes(e.response.status) || (e.response.status === 400 && /incomplete/i.test(e.response.data?.detail || ""));

// Human-readable reason for a failed upload (never a bare "Something went wrong")
export function uploadError(e) {
  const d = e?.response?.data?.detail;
  const st = e?.response?.status;
  if (st === 401) return "You were signed out — sign in again, then tap Retry";
  if (typeof d === "string" && d) return d;
  if (st === 413) return "This file is too large for a single request — tap Retry";
  if (st === 401) return "You were signed out — sign in again, then tap Retry";
  if (st) return `Upload stopped (server error ${st}) — tap Retry to resume`;
  if (e?.name === "NotReadableError" || e?.name === "NotFoundError") return "Your phone stopped sharing this file — pick it again to resume";
  if (e?.code === "ECONNABORTED") return "Connection too slow — tap Retry to resume where it stopped";
  if (navigator.onLine === false) return "You're offline — it will resume when you reconnect";
  return "Connection dropped — tap Retry to resume where it stopped";
}

async function withRetry(fn, tries = 8) {
  for (let t = 0; ; t++) {
    try { return await fn(); }
    catch (e) {
      if (t >= tries || !retryable(e)) throw e;
      await waitOnline();
      await sleep(Math.min(15000, 1500 * 2 ** t));
    }
  }
}

export async function uploadFile(file, opts, onProgress) {
  const fp = await fingerprint(file);
  const { data } = await withRetry(() => api.post("/cloud/uploads/init", { name: file.name, size: file.size, mime: mimeOf(file), fingerprint: fp, last_modified: file.lastModified, album_id: opts.album_id || null, source: opts.source || "upload" }, { timeout: 60000 }));
  if (data.duplicate) return { duplicate: true, file: data.file };
  const ps = data.part_size;
  const n = Math.max(1, Math.ceil(file.size / ps));
  const have = new Set(data.parts);
  let sent = [...have].reduce((a, i) => a + Math.min(ps, file.size - i * ps), 0);
  const inflight = {};
  const report = () => onProgress((sent + Object.values(inflight).reduce((x, y) => x + y, 0)) / file.size, "uploading");
  report();
  const todo = [];
  for (let i = 0; i < n; i++) if (!have.has(i)) todo.push(i);
  // two parts in flight: faster on mobile uplinks without overloading the server
  const worker = async () => {
    while (todo.length) {
      const i = todo.shift();
      const buf = await file.slice(i * ps, Math.min(file.size, (i + 1) * ps)).arrayBuffer(); // read once so a flaky picker URI fails here
      await withRetry(() => api.put(`/cloud/uploads/${data.file_id}/parts/${i}`, buf, {
        headers: { "Content-Type": "application/octet-stream" }, timeout: 240000,
        onUploadProgress: (ev) => { inflight[i] = Math.min(ev.loaded, buf.byteLength); report(); },
      }));
      delete inflight[i];
      sent += buf.byteLength;
      report();
    }
  };
  await Promise.all([worker(), worker()]);
  onProgress(1, "saving");
  const res = (await withRetry(() => api.post(`/cloud/uploads/${data.file_id}/complete`, null, { timeout: 60000 }))).data;
  if (res?.file?.status !== "ready") throw new Error("The server didn't confirm the file was saved");
  try { const th = await makeThumb(file); if (th) { const fd = new FormData(); fd.append("file", th, "thumb.jpg"); await api.post(`/cloud/files/${data.file_id}/thumb`, fd, { timeout: 60000 }); res.file.thumb_url = `/api/cloud/files/${data.file_id}/thumb`; res.file.has_thumb = true; } } catch {}
  return res;
}

export const fmtBytes = (b) => { if (!b) return "0 B"; const u = ["B", "KB", "MB", "GB", "TB"]; const i = Math.min(4, Math.floor(Math.log(b) / Math.log(1024))); return `${(b / 1024 ** i).toFixed(i > 1 ? 1 : 0)} ${u[i]}`; };
